import React, {
  RefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import isNil from 'lodash/isNil';
import IconButton from '@mui/material/IconButton';
import Popover from 'core/components/Popover';
import CloseIcon from '@mui/icons-material/Close';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import TextAreaWithActions from 'pages/Dashboard/components/TextAreaWithActions';
import Button from 'pages/Dashboard/components/Button';
import {
  useSaveClaimNotes,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  billingEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useConfirmDialog,
  useNotify,
} from 'core/hooks';
import clsx from 'clsx';
import {
 isEmptyString,
} from '@populateui/utils';

type FormData = { notes: string };

type Part = 'popover' | 'form';

type Props = {
  claimId: number;
  encounterId: number;
  notes: string;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  classes?: Partial<Record<Part, string>>;
  onSave?: () => void;
};

type NotesFormProps = Omit<Props, 'anchorEl'>;

function NotesForm({
  claimId,
  encounterId,
  notes,
  onClose,
  onSave,
}: NotesFormProps) {
  const notify = useNotify();
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useFormContext<FormData>();

  const {
    mutateAsync: saveClaimNotes,
    isPending: isSaving,
  } = useSaveClaimNotes();

  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textareaElement = inputRef?.current;
    const notesLength = notes?.length ?? 0;
    if (!isNil(textareaElement)) {
      textareaElement.focus();
      textareaElement.setSelectionRange(notesLength, notesLength);
    }
  }, []);

  const onUpdate = async ({ notes }: FormData) => {
    await saveClaimNotes({
      claimId,
      notes: isEmptyString(notes) ? null : notes,
    });
    billingEventChannel.emit('onChargesInfoChange', {
      id: claimId,
      encounterId,
    });
    notify({
      message: 'Notes saved successfully',
      variant: 'success',
    });
    onSave?.();
  };

  return (
    <div className="p-4 flex flex-col gap-4 z-10">
      <div className="flex justify-between items-center">
        <div className="font-semibold text-sm">
          Claim Notes
        </div>
        <div>
          <IconButton
            size="small"
            disableRipple
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            <CloseIcon className="!w-5 !h-5 text-gray-700" />
          </IconButton>
        </div>
      </div>
      <Controller
        control={control}
        name="notes"
        render={({ field }) => (
          <TextAreaWithActions
            {...field}
            placeholder="Add Notes"
            maxRows={5}
            rows={3}
            id="claim-notes"
            trackingLabel="Claim Notes"
            className="!max-h-[120px] pr-1"
            inputRef={inputRef as RefObject<HTMLTextAreaElement>}
          />
        )}
      />
      <div className="flex justify-between items-center gap-2">
        <Button
          variant="outlined"
          type="button"
          className="flex-1 !max-w-[unset]"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          className="flex-1 !max-w-[unset]"
          color="primary"
          type="submit"
          disabled={!isValid || isSaving}
          onClick={(e) => {
            e.stopPropagation();
            handleSubmit(onUpdate)();
          }}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

export default function ClaimNotesPopover({
  claimId,
  encounterId,
  notes,
  anchorEl,
  onClose,
  classes,
}: Props) {
  const { confirm, closeConfirm } = useConfirmDialog();
  const formProps = useForm<FormData>({
    defaultValues: { notes: notes ?? '' },
  });

  const { formState: { isDirty } } = formProps;

  const handleClose = useCallback(() => {
    if (isDirty) {
      confirm({
        title: 'Discard changes?',
        confirmText: 'Keep Editing',
        cancelText: 'Close',
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
        hideCloseBtn: true,
        onClose: () => {
          onClose();
          closeConfirm();
        },
        onConfirm: () => {
          closeConfirm();
        },
        content: (
          <p>
            Your note has unsaved changes. Do you want to close without saving?
          </p>
        ),
      });
    } else {
      onClose();
    }
  }, [onClose, isDirty]);

  return (
    <Popover
      anchorEl={anchorEl}
      onClose={handleClose}
      classes={{ paper: clsx('!w-[520px]', classes?.popover) }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <FormProvider {...formProps}>
        <NotesForm
          claimId={claimId}
          encounterId={encounterId}
          notes={notes ?? ''}
          onClose={handleClose}
          onSave={onClose}
        />
      </FormProvider>
    </Popover>
  );
}
